/*
 * Copyright (C) Fraunhofer IESE 2021-2025 - Mher Ter-Tovmasyan
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import { getIcon, isEmptyOrNull, SLRLoadingButton } from '@SLR/shared-library';
import { OrganizationType } from '@SLR/user-service-full-sdk';
import { DEDI_ACCESS_URL, OnboardingType } from 'configs';
import { useCreateOrganizationDraft } from 'hooks/organization-draft/useCreateOrganizationDraft';
import { ScrollToTop } from 'layout/ScrollToTop';
import { useQueryParam } from 'providers';
import { FC, useCallback, useMemo } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { DraftForm } from '../form/DraftFrom';
import {
  OrganizationDraftInput,
  OrganizationDraftSchema
} from '../schema/organizationDraft';

import './DraftPage.scss';

export const DraftPage: FC = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'draftPage'
  });
  const { onboardingQuery } = useQueryParam();
  const { onboardingType, redirectURL } = onboardingQuery ?? {};

  const form = useForm<OrganizationDraftInput>({
    resolver: yupResolver(OrganizationDraftSchema(onboardingType!))
  });

  const {
    mutate: createDraft,
    isPending,
    isSuccess,
    isError
  } = useCreateOrganizationDraft();

  const organizationType = useMemo(
    () =>
      onboardingType === OnboardingType.SOLUTION_PROVIDER
        ? OrganizationType.SolutionProvider
        : OrganizationType.Customer,
    [onboardingType]
  );

  const handleSubmitDraft = useCallback(
    (draft: OrganizationDraftInput) => {
      const memberEmailAddresses = (
        draft.emails?.map(({ email }) => email) ?? []
      ).filter((email) => !isEmptyOrNull(email));
      memberEmailAddresses.push(draft.contactEmailAddress ?? '');

      createDraft({
        createOrganizationDraftRequestDto: {
          additionalInformation: draft.additionalInformation ?? undefined,
          contactEmailAddress: draft.contactEmailAddress ?? '',
          contactPerson: draft.contactPerson ?? '',
          geoAreaId: draft.geoAreaId ?? undefined,
          geoAreaIdOrAdditionalInformationSpecified:
            !isEmptyOrNull(draft.geoAreaId) ||
            !isEmptyOrNull(draft.additionalInformation),
          memberEmailAddresses,
          name: draft.name ?? '',
          notUserOrganization: true,
          solutionInformation: draft.solutionInformation ?? undefined,
          solutionInformationSpecified: !isEmptyOrNull(
            draft.solutionInformation
          ),
          type: organizationType,
          website: draft.website ?? undefined,
          description: draft.description ?? ''
        }
      });
    },
    [createDraft, organizationType]
  );

  return (
    <Container className="draft-page">
      <Row className="gap-4 text-center pb-4">
        <Col xs="12">
          <span className="title">
            <Trans t={t} i18nKey={`pageHead.${onboardingType!}.title`} />
          </span>
        </Col>
        <Col xs="12">
          <span className="subtitle">
            {t(`pageHead.${onboardingType!}.subtitle`)}
          </span>
        </Col>
        {!isSuccess && (
          <Col xs="12" className="py-4">
            <span className="description">
              {t(`pageHead.${onboardingType!}.description`)}
            </span>
          </Col>
        )}
      </Row>
      {isSuccess ? (
        <>
          <ScrollToTop trigger={isSuccess} />
          <Row className="gap-5 px-2 px-sm-4 text-center success">
            <Col xs="12" className="success-title">
              {t('success.title')}
            </Col>
            <Col xs="12" className="py-3">
              <FontAwesomeIcon
                icon={getIcon('fal', 'paper-plane')}
                size="7x"
                className="text-success"
              />
            </Col>
            <Col xs="12" className="success-content">
              {t('success.content')}
            </Col>
            <Col xs="12">
              <Button
                href={redirectURL ?? DEDI_ACCESS_URL}
                title={t('success.confirm')}
                size="lg"
              >
                {t('success.confirm')}
              </Button>
            </Col>
          </Row>
        </>
      ) : (
        <FormProvider {...form}>
          <Row className="form-card p-5">
            <DraftForm onboardingType={onboardingType!} isError={isError} />

            <Col
              xs="12"
              className="pt-5 my-2 d-flex align-items-center justify-content-center"
            >
              <SLRLoadingButton
                isLoading={isPending}
                onClick={form.handleSubmit(handleSubmitDraft)}
              >
                {t('send')}
              </SLRLoadingButton>
            </Col>
          </Row>
        </FormProvider>
      )}
    </Container>
  );
};
